import { Bot } from "lucide-react"
import { Link } from "react-router-dom"

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold">{title}</h2>
      {children}
    </section>
  )
}

export function PrivacyPage() {
  return (
    <div className="bg-background min-h-full">
      <header className="mx-auto flex max-w-3xl items-center gap-2 p-6">
        <div className="bg-primary text-primary-foreground flex size-8 items-center justify-center rounded-lg">
          <Bot className="size-4" />
        </div>
        <Link to="/" className="font-semibold">
          AI Agents
        </Link>
      </header>

      <main className="mx-auto max-w-3xl space-y-8 px-6 pb-16 text-sm leading-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Privacy policy</h1>
          <p className="text-muted-foreground">Last updated: 13 September 2026</p>
        </div>

        <p>
          AI Agents (
          <a href="https://agents-fabric.uz" className="underline underline-offset-4">
            agents-fabric.uz
          </a>
          ) is a platform for creating personal AI assistants and connecting them to tools
          you register. This policy explains what data the service stores and how it is
          used.
        </p>

        <Section title="Data we collect">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Account data.</strong> Your email address, an optional display name
              and, if you sign up with a password, a hash of that password (the password
              itself is never stored).
            </li>
            <li>
              <strong>Google sign-in.</strong> If you sign in with Google, we receive your
              Google account id, email address and name from Google. We use them only to
              create and identify your account. We do not receive or store your Google
              password, contacts, files or any other Google data.
            </li>
            <li>
              <strong>Content you create.</strong> Agent configurations, chat messages,
              schedules and the tool connections you register. Credentials for your tool
              connections (such as API tokens for MCP servers) are stored encrypted.
            </li>
            <li>
              <strong>Run records.</strong> When an agent runs, the service records the
              conversation, tool calls and token usage so you can inspect what happened.
            </li>
          </ul>
        </Section>

        <Section title="How data is used">
          <p>
            Your data is used solely to operate the service for you: authenticating you,
            running your agents, calling the tools you connected and showing you your own
            history. Chat content is sent to the model provider configured for your agent
            in order to generate responses.
          </p>
        </Section>

        <Section title="Google user data">
          <p>
            AI Agents&apos; use and transfer of information received from Google APIs adheres
            to the{" "}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              className="underline underline-offset-4"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements. The Google account id, email address
            and name received at sign-in are used only to create and identify your account;
            they are never shared with third parties or used for advertising.
          </p>
        </Section>

        <Section title="What we do not do">
          <ul className="list-disc space-y-2 pl-5">
            <li>We do not sell or share your personal data with third parties.</li>
            <li>We do not use your data for advertising.</li>
            <li>
              We do not read your conversations except as needed to operate the service.
            </li>
          </ul>
        </Section>

        <Section title="Cookies and local storage">
          <p>
            The app stores an access token in your browser&apos;s local storage to keep you
            signed in. No tracking or advertising cookies are used.
          </p>
        </Section>

        <Section title="Data retention and deletion">
          <p>
            Your data is kept while your account exists. To delete your account and all
            associated data, contact us at the address below.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions or deletion requests:{" "}
            <a
              href="mailto:ibrohimbekjalolov2110@gmail.com"
              className="underline underline-offset-4"
            >
              ibrohimbekjalolov2110@gmail.com
            </a>
          </p>
        </Section>
      </main>

      <footer className="text-muted-foreground mx-auto flex max-w-3xl items-center justify-between border-t p-6 text-sm">
        <span>© {new Date().getFullYear()} AI Agents</span>
        <Link to="/" className="hover:text-foreground underline underline-offset-4">
          Home
        </Link>
      </footer>
    </div>
  )
}
